import {
  EntityCustomViewParams,
  useAuthController,
  useDataSource,
  useSnackbarController,
} from "@firecms/core";
import { Alert, Chip, LoadingButton, Typography } from "@firecms/ui";
import { useEffect, useState } from "react";

import { hasAnyRole, ONBOARDING_QUEUE_ROLES } from "./apple_delegated_onboarding.roles";
import {
  AppleDelegatedOnboardingEntity,
  AppleEnvironmentEnum,
  AppleEnvScoped,
  ONBOARDING_MAX_ATTEMPTS,
  ONBOARDING_MIRRORED_STEP_NAMES,
  ONBOARDING_STEP_NAMES,
  OnboardingGalleryPhotoEntity,
  OnboardingShowcaseRequestEntity,
  OnboardingStepStatus,
  OnboardingSubmitStateEnum,
} from "./apple_delegated_onboarding.types";
import {
  describeMirrorFreshness,
  describeUnknownFailureCode,
  evaluateQueueGate,
  isKnownFailureCode,
  QueueTarget,
  queueTargetLabel,
  resolveDocumentEnvironment,
  resolveEnvBucket,
} from "./apple_delegated_onboarding.utils";
import {
  fetchOnboardingParent,
  queueOnboardingStep,
  reopenOnboardingStep,
} from "./apple_delegated_onboarding.write";
import { BrandAssetPairStateChip, OnboardingStatusChip } from "./OnboardingStepChip";

/**
 * Any step-shaped value this view renders: the shared status fields, the staff input (opaque here —
 * the form renders it), and the env-scoped Apple result. `unknown` for the two halves this view only
 * passes through, so a field the backend adds needs no change on this side.
 */
type StepLike = OnboardingStepStatus & {
  input?: unknown;
  companyId?: string | null;
  apple?: AppleEnvScoped<unknown> | null;
};

/**
 * THE STAFF-FACING SURFACE — one panel per step, each with its live status, Apple's own words when
 * something failed, and a queue button that is either pressable or visibly refused WITH THE REASON.
 *
 * ⚠️ WHY A CUSTOM VIEW AND NOT ONLY ENTITY ACTIONS. Design §0 #6 makes "every button is gated with a
 * stated reason" the highest-value thing in this build, and `EntityAction` in FireCMS 3.0.0-beta.14
 * has no `isEnabled`/`disabled` predicate (only `name`, `key`, `icon`, `onClick`, `collapsed`,
 * `includeInForm`) — the same limitation documented at `apple_grant_status.ts:28-31`. A component
 * can render a disabled button next to its reason, which is strictly better than learning the reason
 * from a snackbar after pressing. The collection ALSO carries entity actions with the identical gate
 * inside `onClick`, for the row menu in the list view where this panel is not on screen; both call
 * `evaluateQueueGate`, so the two cannot disagree.
 */
export function OnboardingStepsView({
  entity,
  formContext,
}: EntityCustomViewParams<Partial<AppleDelegatedOnboardingEntity>>) {
  if (!entity) {
    return (
      <div className="p-8">
        <Alert color="info">
          Save this onboarding record first. A step can only be queued on a document that exists —
          the backend drain finds its work by querying saved documents.
        </Alert>
      </div>
    );
  }

  const values = entity.values;
  const env = resolveDocumentEnvironment(values);

  return (
    <div className="flex flex-col gap-6 p-8">
      <EnvironmentBanner env={env} />
      {formContext.formex.dirty && (
        <Alert color="warning">
          You have unsaved changes on this form. The buttons below read the SAVED document, and so
          does the backend — save before queueing, or the drain runs against the old values.
        </Alert>
      )}
      {ONBOARDING_STEP_NAMES.map((step) => (
        <StepPanel
          key={step}
          target={step}
          status={values[step]}
          parent={values}
          env={env}
          path={entity.path}
          entityId={entity.id}
        />
      ))}
    </div>
  );
}

/**
 * The showcase subcollection's equivalent panel.
 *
 * ⚠️ Its status fields sit at the DOCUMENT ROOT, so `status` and `parent` come from two different
 * documents: the request itself, and the onboarding document above it — which is FETCHED, because
 * FireCMS hands a subcollection its parent's PATH but never its values, and the brand and location
 * id pins cannot be checked without them.
 */
export function OnboardingShowcaseView({
  entity,
}: EntityCustomViewParams<Partial<OnboardingShowcaseRequestEntity>>) {
  return (
    <SubcollectionQueueView
      target="showcase"
      entity={entity}
      unsavedMessage="Save this showcase request first. It can only be queued once the document exists."
    />
  );
}

/**
 * The gallery subcollection's equivalent panel — ONE photo per document, one panel per photo.
 *
 * ⚠️ IT IS THE SAME COMPONENT AS THE SHOWCASE VIEW, differing only in the target and the empty-state
 * sentence, and that is on purpose. Both are documents whose status sits at their ROOT and whose gate
 * needs the parent's env buckets; a second hand-written copy of the fetch-parent / render-panel dance
 * is exactly how two surfaces start disagreeing about what "queueable" means. FireCMS needs a
 * distinct `Builder` per entity view — hence two thin exported wrappers rather than one parameterised
 * component registered twice.
 */
export function OnboardingGalleryPhotoView({
  entity,
}: EntityCustomViewParams<Partial<OnboardingGalleryPhotoEntity>>) {
  return (
    <SubcollectionQueueView
      target="galleryPhoto"
      entity={entity}
      unsavedMessage="Save this gallery photo first. It can only be queued once the document exists."
    />
  );
}

/**
 * The shared body of the two subcollection panels: fetch the parent, name the environment, render
 * the one gated `StepPanel`.
 *
 * The parent FETCH is the whole reason this is not just `StepPanel`: neither FireCMS's custom-view
 * props nor its entity-action props hand a subcollection its parent's values, only the path, and
 * every cross-step pin these two gates apply lives on the parent's env-scoped buckets. A failed read
 * leaves `parent` as `null`, which the gates degrade on with a stated caution rather than a refusal.
 */
function SubcollectionQueueView({
  target,
  entity,
  unsavedMessage,
}: {
  target: QueueTarget;
  entity: EntityCustomViewParams<Partial<StepLike>>["entity"];
  unsavedMessage: string;
}) {
  const dataSource = useDataSource();
  const [parent, setParent] = useState<Partial<AppleDelegatedOnboardingEntity> | null>(null);
  const childPath = entity?.path;

  useEffect(() => {
    let cancelled = false;
    void fetchOnboardingParent(dataSource, childPath).then((values) => {
      if (!cancelled) setParent(values);
    });
    return () => {
      cancelled = true;
    };
  }, [dataSource, childPath]);

  if (!entity) {
    return (
      <div className="p-8">
        <Alert color="info">{unsavedMessage}</Alert>
      </div>
    );
  }

  const env = resolveDocumentEnvironment(parent);

  return (
    <div className="flex flex-col gap-6 p-8">
      <EnvironmentBanner env={env} />
      <StepPanel
        target={target}
        status={entity.values}
        parent={parent}
        env={env}
        path={entity.path}
        entityId={entity.id}
      />
    </div>
  );
}

/**
 * Names the environment every result and gate below is talking about.
 *
 * ⚠️ EXPLICIT RATHER THAN IMPLICIT, because the alternative is actively misleading: `submitState` is
 * flat while results live under `apple.<ENV>`, so one DQE run makes every environment read
 * "Submitted to Apple". The deployed backend writes DQE today, and nothing in this CMS can observe
 * that — hence the environment is derived from the document and printed, never assumed.
 */
function EnvironmentBanner({ env }: { env: AppleEnvironmentEnum | null }) {
  if (!env) {
    return (
      <Alert color="base">
        No Apple result is recorded on this record in any environment yet, so nothing below has run.
      </Alert>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Typography variant="label" color="secondary">
        Apple environment shown
      </Typography>
      <Chip
        colorScheme={env === AppleEnvironmentEnum.PRODUCTION ? "greenDark" : "blueLight"}
        size="small"
      >
        {env}
      </Chip>
      <Typography variant="caption" color="secondary">
        Results are recorded per environment; the intent status is not. A step run in one
        environment reads &ldquo;Submitted&rdquo; in all of them, so read the Apple result below —
        not the status chip — to answer &ldquo;is this live?&rdquo;.
      </Typography>
    </div>
  );
}

type StepPanelProps = {
  target: QueueTarget;
  status: StepLike | null | undefined;
  parent: Partial<AppleDelegatedOnboardingEntity> | null;
  env: AppleEnvironmentEnum | null;
  path: string;
  entityId: string;
};

function StepPanel({ target, status, parent, env, path, entityId }: StepPanelProps) {
  const dataSource = useDataSource();
  const snackbarController = useSnackbarController();
  const authController = useAuthController();
  const [pending, setPending] = useState(false);

  const label = queueTargetLabel(target);
  const gate = evaluateQueueGate({ target, step: status, parent, env });
  const canQueue = hasAnyRole(authController, ONBOARDING_QUEUE_ROLES);
  const parked = status?.submitState === OnboardingSubmitStateEnum.NEEDS_ATTENTION;

  // Both writes share this shape, and both go through the one choke point in
  // `apple_delegated_onboarding.write.ts` — no component composes a Firestore value itself.
  const run = async (action: typeof queueOnboardingStep) => {
    setPending(true);
    try {
      await action({ dataSource, snackbarController }, { path, entityId, queueTarget: target });
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-slate-200 p-4 dark:border-slate-800">
      <div className="flex flex-wrap items-center gap-3">
        <Typography variant="subtitle1">{label}</Typography>
        <OnboardingStatusChip status={status} />
        <Typography variant="caption" color="secondary">
          {status?.attempts ?? 0} of {ONBOARDING_MAX_ATTEMPTS} attempts used
        </Typography>
      </div>

      <WriteErrorNotice writeError={status?.writeError} />
      <FailureNotice status={status} />
      <MirrorNotice target={target} status={status} />
      <BrandAssetPairState target={target} status={status} />
      <AppleResult status={status} />
      <BookingOmittedNotice target={target} parent={parent} env={env} />

      {!gate.allowed && <Alert color="warning">{gate.reason}</Alert>}
      {gate.allowed &&
        gate.cautions.map((caution) => (
          <Alert key={caution} color="info">
            {caution}
          </Alert>
        ))}
      {!canQueue && (
        <Alert color="base">
          You do not have permission to queue Apple onboarding steps. Ask an admin.
        </Alert>
      )}

      <div className="flex flex-wrap gap-2">
        <LoadingButton
          variant="filled"
          loading={pending}
          disabled={!gate.allowed || !canQueue}
          onClick={() => run(queueOnboardingStep)}
        >
          Queue {label.toLowerCase()}
        </LoadingButton>
        {parked && canQueue && (
          <LoadingButton
            variant="outlined"
            loading={pending}
            onClick={() => run(reopenOnboardingStep)}
          >
            Re-open as draft
          </LoadingButton>
        )}
      </div>
    </div>
  );
}

/** "I could not even enqueue" — a CMS-owned note the backend never reads. */
function WriteErrorNotice({ writeError }: { writeError: string | null | undefined }) {
  if (!writeError) return null;
  return (
    <Alert color="error">
      <Typography variant="body2">
        The last attempt to queue this step failed before anything was written:{" "}
        <span className="font-mono">{writeError}</span>. Nothing was sent to Apple. This note is
        cleared the next time the step is queued successfully.
      </Typography>
    </Alert>
  );
}

/**
 * The failure block.
 *
 * ⚠️ `failureMessage` IS RENDERED VERBATIM, ALWAYS. It is written by the backend for staff, it is
 * keyed on the exact state, and it is the whole product of a blocked step. This console never maps a
 * failure code to copy of its own — the code enum is explicitly unstable (20 values, grown twice
 * recently), so a local mapping would go stale silently. An unrecognised code gets ONE extra line
 * above the message saying it is newer than this screen; the message itself is untouched.
 */
function FailureNotice({ status }: { status: OnboardingStepStatus | null | undefined }) {
  const failureCode = status?.failureCode;
  const failureMessage = status?.failureMessage;
  const submitState = status?.submitState;
  if (!failureCode && !failureMessage) return null;
  const unknown = Boolean(failureCode) && !isKnownFailureCode(failureCode);
  return (
    <Alert color={submitState === OnboardingSubmitStateEnum.NEEDS_ATTENTION ? "error" : "warning"}>
      <div className="flex flex-col gap-1">
        {failureCode && (
          <Typography variant="caption" className="font-mono">
            {failureCode}
          </Typography>
        )}
        {unknown && failureCode && (
          <Typography variant="caption" color="secondary">
            {describeUnknownFailureCode(failureCode, submitState)}
          </Typography>
        )}
        <Typography variant="body2">
          {failureMessage ??
            "The backend recorded a failure code with no message. Ask engineering to look at this step."}
        </Typography>
      </div>
    </Alert>
  );
}

/**
 * Staleness for the MIRRORED steps only. An absent `mirroredAt` means create-time seed, not live.
 *
 * ⚠️ GATED ON `ONBOARDING_MIRRORED_STEP_NAMES`, NOT RENDERED FOR EVERYTHING. `location`,
 * `locationCta` and `showcase` are not mirrored at all, so "the status mirror has not refreshed it
 * yet" would be permanently true, permanently useless, and would contradict what the gate copy tells
 * staff a few lines lower. A notice that always fires is a notice nobody reads.
 *
 * Derived from the list rather than a hardcoded step check on purpose: a backend ticket in flight
 * extends the mirror to cover `location`, and adding that name to
 * `ONBOARDING_MIRRORED_STEP_NAMES` is then the only change needed here.
 */
function MirrorNotice({
  target,
  status,
}: {
  target: QueueTarget;
  status: StepLike | null | undefined;
}) {
  const mirrored = (ONBOARDING_MIRRORED_STEP_NAMES as readonly string[]).includes(target);
  if (!mirrored) return null;
  const resolved = resolveEnvBucket(status?.apple);
  if (!resolved) return null;
  const notice = describeMirrorFreshness(resolved.bucket, new Date());
  if (!notice) return null;
  return <Alert color="info">{notice}</Alert>;
}

/**
 * The brand-asset PAIR state, as one chip, on the brand-assets panel only.
 *
 * ⚠️ THE BACKEND COMPUTES IT; THIS RENDERS IT. `pairState` already appears in the generic
 * `AppleResult` line below as raw text, which is honest but easy to miss — and the one thing staff
 * most need from this step is "is the branding live, or is the place card showing nothing?". So it is
 * lifted to a chip. No truth table is duplicated here: the value is read straight off the bucket.
 *
 * `INCOMPLETE` additionally gets a loud notice, because it is the state that most reads as progress
 * and is not: exactly one asset exists, and Apple renders the branding as a set, so the place card
 * shows NO branding at all.
 */
function BrandAssetPairState({
  target,
  status,
}: {
  target: QueueTarget;
  status: StepLike | null | undefined;
}) {
  if (target !== "brandAssets") return null;
  const resolved = resolveEnvBucket(status?.apple);
  const pairState = (resolved?.bucket as { pairState?: string | null } | undefined)?.pairState;
  if (!pairState) return null;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Typography variant="label" color="secondary">
          Branding on Apple
        </Typography>
        <BrandAssetPairStateChip pairState={pairState} />
      </div>
      {pairState === "INCOMPLETE" && (
        <Alert color="error">
          Only ONE of the two images exists at Apple. Apple shows a practice&rsquo;s branding as a
          set, so this place card is currently displaying NO branding at all — this is a live,
          client-visible gap, not a step still in progress. Attach the missing image and re-queue.
        </Alert>
      )}
    </div>
  );
}

/**
 * The three array fields the backend records on the Apple bucket. Rendered by `AppleResultArrays`
 * below; kept as a named list so the scalar line and the array blocks cannot both claim the same key.
 */
const APPLE_RESULT_ARRAY_FIELDS = ["gaps", "validationReports", "offeredCtas"] as const;

const APPLE_RESULT_ARRAY_LABELS: Record<(typeof APPLE_RESULT_ARRAY_FIELDS)[number], string> = {
  gaps: "Data-quality notes on what we sent",
  validationReports: "Apple's own validation messages",
  offeredCtas: "Calls to action Apple offered",
};

/** A string worth showing: non-empty once trimmed. */
function presentableText(value: unknown): string | null {
  if (typeof value === "string" && value.trim().length > 0) return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return null;
}

/**
 * One array element → one line of readable text, WITHOUT assuming its shape.
 *
 * 🛑 The shape assumption is the thing to get right here. The backend types these arrays
 * `z.array(z.unknown())` on purpose — "Apple owns the report shape, and an unexpected field must not
 * make the whole document unparseable" (`schemas/apple-delegated-onboarding.schema.ts:555-560`). A
 * renderer that destructured `{ field, severity, reason }` would re-introduce exactly the coupling
 * that comment exists to prevent, one layer up: the day Apple adds or renames a key, this panel
 * either crashes the whole step view or prints `[object Object]`.
 *
 * So every branch degrades instead of failing, and the LAST branch never hides the fact that data is
 * present — an element this function does not recognise is still shown, as compact JSON, because
 * "there is something here we could not read" is information a staff member needs and silence is not.
 */
function describeAppleResultElement(element: unknown): string | null {
  const direct = presentableText(element);
  if (direct) return direct;
  if (element === null || element === undefined) return null;
  if (typeof element !== "object") return null;

  const record = element as Record<string, unknown>;
  // The `gaps` shape (`{ field, severity, reason }`) and Apple's report shape (`message`) are both
  // read field-by-field, so an element missing any of them still renders whatever it does have.
  const field = presentableText(record.field);
  const reason = presentableText(record.reason) ?? presentableText(record.message);
  if (field && reason) return `${field} — ${reason}`;
  if (reason) return reason;
  if (field) return field;

  // Unrecognised, but non-empty: say so rather than dropping it.
  try {
    const json = JSON.stringify(element);
    if (json && json !== "{}" && json !== "[]") return json;
  } catch {
    // Circular or otherwise unserialisable — fall through to the generic notice below.
  }
  return "(unreadable entry — ask engineering)";
}

/** `severity` if the element happens to carry a recognised one; `null` otherwise. */
function severityOf(element: unknown): "REQUIRED" | "RECOMMENDED" | null {
  if (element === null || typeof element !== "object") return null;
  const severity = (element as Record<string, unknown>).severity;
  return severity === "REQUIRED" || severity === "RECOMMENDED" ? severity : null;
}

type AppleResultLine = { severity: "REQUIRED" | "RECOMMENDED" | null; text: string };
type AppleResultBlock = {
  key: (typeof APPLE_RESULT_ARRAY_FIELDS)[number];
  lines: AppleResultLine[];
};

/**
 * The three arrays the backend records, rendered as readable lists.
 *
 * ⚠️ WHY THIS EXISTS AT ALL. The backend has written `gaps` and `validationReports` since the drain
 * shipped and the CMS has typed them since — and nothing rendered them. On the FAILURE path staff
 * still see them, because the drain flattens them into the step's `message`
 * (`drain-location-step.ts:271-277`). What was invisible is a **RECOMMENDED gap on a SUCCESSFUL
 * create**: no message is generated on that path, and `AppleResult`'s scalar filter dropped the
 * array. That is precisely the shape of Apple's own accepted-but-imperfect advisories — "we
 * published this location, and six of its seven opening days made it".
 *
 * ⚠️ AND WHY HERE RATHER THAN AS FORM PROPERTIES. `AppleResult` already resolves the env bucket for
 * every step, so extending it covers all six steps at once; declaring read-only `buildProperty`
 * entries would mean three arrays × six steps of duplicated config that drifts one step at a time.
 *
 * ⚠️ A RECOMMENDED GAP IS NOT AN ERROR TREATMENT. Apple accepted the location. Rendering an advisory
 * in `Alert color="error"` would tell staff something is broken when nothing is — so severity is
 * distinguished by a label, and the block stays informational.
 *
 * The line collection is a plain function rather than part of the component so `AppleResult` can ask
 * whether there is anything to show WITHOUT rendering: a JSX element is always truthy, so emptiness
 * has to be computed, not inferred from the element.
 */
function collectAppleResultArrayBlocks(bucket: Record<string, unknown>): AppleResultBlock[] {
  const blocks: AppleResultBlock[] = [];
  for (const key of APPLE_RESULT_ARRAY_FIELDS) {
    const raw = bucket[key];
    if (!Array.isArray(raw) || raw.length === 0) continue;
    const lines: AppleResultLine[] = [];
    for (const element of raw as unknown[]) {
      const text = describeAppleResultElement(element);
      if (text) lines.push({ severity: severityOf(element), text });
    }
    if (lines.length > 0) blocks.push({ key, lines });
  }
  return blocks;
}

function AppleResultArrays({ blocks }: { blocks: AppleResultBlock[] }) {
  if (blocks.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      {blocks.map(({ key, lines }) => (
        <div key={key} className="flex flex-col gap-1">
          <Typography variant="label" color="secondary">
            {APPLE_RESULT_ARRAY_LABELS[key]}
          </Typography>
          <ul className="flex list-disc flex-col gap-1 pl-4">
            {lines.map((line, index) => (
              <li key={`${key}-${index}`}>
                <Typography variant="caption" color="secondary">
                  {line.severity && (
                    <span className="font-mono font-semibold">{line.severity}: </span>
                  )}
                  {line.text}
                </Typography>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/**
 * Apple's own result for the resolved environment: the bucket's scalar fields rendered generically
 * (so a field the backend adds shows up without a CMS change), plus the `gaps` /
 * `validationReports` / `offeredCtas` arrays rendered as readable lists by `AppleResultArrays`.
 *
 * ⚠️ Arrays are rendered HERE, not by read-only properties in the form. No such properties exist —
 * a grep for `gaps` across the four onboarding collection configs returns nothing — and the previous
 * version of this comment claimed they did while the scalar filter below silently dropped every
 * array. One component covers all six steps; per-step properties would not.
 */
function AppleResult({ status }: { status: StepLike | null | undefined }) {
  const resolved = resolveEnvBucket(status?.apple);
  if (!resolved) return null;
  const bucket = (resolved.bucket ?? {}) as Record<string, unknown>;
  const entries = Object.entries(bucket).filter(
    ([, value]) =>
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean" ||
      value instanceof Date,
  );
  const blocks = collectAppleResultArrayBlocks(bucket);
  if (entries.length === 0 && blocks.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      {entries.length > 0 && (
        <div className="flex flex-col gap-1">
          <Typography variant="label" color="secondary">
            Apple result recorded in {resolved.env}
          </Typography>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            {entries.map(([key, value]) => (
              <Typography key={key} variant="caption" color="secondary">
                <span className="font-mono">{key}</span>:{" "}
                {value instanceof Date ? value.toLocaleString() : String(value)}
              </Typography>
            ))}
          </div>
        </div>
      )}
      <AppleResultArrays blocks={blocks} />
    </div>
  );
}

/**
 * ✅ Recorded rather than inferred, precisely so a human can see it — so this makes it loud instead
 * of leaving it as one entry in the generic list above.
 *
 * `bookingOmitted` means the location create went out WITHOUT the booking quicklink because no
 * `bookingUrl` was mapped. The place card is valid and has NO "Schedule" button, which is the entire
 * point of the feature.
 */
function BookingOmittedNotice({
  target,
  parent,
  env,
}: {
  target: QueueTarget;
  parent: Partial<AppleDelegatedOnboardingEntity> | null;
  env: AppleEnvironmentEnum | null;
}) {
  if (target !== "location" && target !== "locationCta") return null;
  if (!env || parent?.location?.apple?.[env]?.bookingOmitted !== true) return null;
  return (
    <Alert color="warning">
      This location was created WITHOUT the booking quicklink, because no booking URL was mapped at
      the time. The place card is valid but has no &ldquo;Schedule&rdquo; button — which is the
      whole point of this feature. Adding one now means updating the live location; ask engineering.
    </Alert>
  );
}
